import { forwardRef, useCallback } from 'react';
import { useIntakeFormShallowStore } from '@frontend/api-intake-form';
import { ActionBar } from '@frontend/action-bar';
import { getUser, isWeaveUser } from '@frontend/auth-helpers';
import { ErrorBoundary, ActionBarBoundary } from '@frontend/error-boundary';
import { QuickFillActionBarToggleButton } from '@frontend/quick-fill';
import { useMatchMedia, breakpoints } from '@frontend/responsiveness';
import { useScopedAppFlagStore } from '@frontend/scope';
import { WeaveActionBar } from '../../action-bar';
import { useTopBarStore, useTopBarShallowStore } from '../store/use-top-bar-store';
import { TopBarFocusIds } from '../utils/focus-registry';
import { SchedulePulseButton } from './schedule-pulse/schedule-pulse-button';
import { TaskCenterButton } from './task-center/task-center-button';
import { TeamChatV2RemasteredButton } from './team-chat/team-chat-remastered-button';

export const ActionBarContentWrappper = () => {
  const { isShowIntakeForm } = useIntakeFormShallowStore('isShowIntakeForm');

  if (isShowIntakeForm) return null;

  return <ActionBarContent />;
};

export const ActionBarContent = () => {
  const { getFeatureFlagValue, getCustomizationFlagValue } = useScopedAppFlagStore();
  const isXSmall = useMatchMedia({ maxWidth: breakpoints.xsmall.max });
  const user = getUser();
  const weaveUser = isWeaveUser();
  const hasChatCustomizationFlag = getCustomizationFlagValue('chat');
  const isTaskCenterAvailable = getFeatureFlagValue('new-task-center-experience');

  const shouldHaveChat = hasChatCustomizationFlag && user && !weaveUser;
  if (isXSmall) return null;
  return (
    <WeaveActionBarWrapper>
      <ErrorBoundary fallback={ActionBarBoundary}>
        {shouldHaveChat ? <TeamChatV2RemasteredButton /> : null}
      </ErrorBoundary>
      {isTaskCenterAvailable && (
        <ErrorBoundary fallback={ActionBarBoundary}>
          <TaskCenterButton />
        </ErrorBoundary>
      )}
      <ErrorBoundary fallback={ActionBarBoundary}>
        <QuickFillActionBarToggleButtonWrapper />
      </ErrorBoundary>
      <ErrorBoundary fallback={ActionBarBoundary}>
        <SchedulePulseButton />
      </ErrorBoundary>
    </WeaveActionBarWrapper>
  );
};

const WeaveActionBarWrapper = ({ children }: { children: React.ReactNode }) => {
  const { isActionMobileOpen, setActionMobileOpen } = useTopBarShallowStore(
    'isActionMobileOpen',
    'setActionMobileOpen'
  );

  return (
    <WeaveActionBar isMobileOpen={isActionMobileOpen} setIsMobileOpen={setActionMobileOpen}>
      {children}
    </WeaveActionBar>
  );
};

type Props = Omit<React.ComponentProps<typeof ActionBar.Action>, 'label' | 'id' | 'icon' | 'isActive' | 'onClick'>;

const QuickFillActionBarToggleButtonWrapper = forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const { isQuickFillOpen, setQuickFillOpen } = useTopBarShallowStore('isQuickFillOpen', 'setQuickFillOpen');

  const handleClick = useCallback(() => {
    setQuickFillOpen(!useTopBarStore.getState().isQuickFillOpen);
  }, [setQuickFillOpen]);

  return (
    <QuickFillActionBarToggleButton
      {...props}
      id={TopBarFocusIds.quickFill}
      ref={ref}
      isActive={isQuickFillOpen}
      onClick={handleClick}
    />
  );
});

QuickFillActionBarToggleButtonWrapper.displayName = 'QuickFillActionBarToggleButtonWrapper';
